import keys from './../../i18n/keys';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { SChangeButton, SChangeWrapper, SCnt, SPeriod, SPeriodValue, Separator } from './styles';
import { ReactComponent as ArrowLeft } from 'assets/new/chevron_left.svg';
import { ReactComponent as ArrowRight } from 'assets/new/chevron_right.svg';
import { PERIOD_OPTIONS } from 'services/consts/periodOptions';
import { useAppDispatch, useAppSelector } from 'services/configuration/store/hooks';
import { getMonths } from 'services/consts/months';
import { PeriodEnum } from 'services/types/PeriodEnum';
import { useTranslation } from 'react-i18next';
import { PeriodType } from './types';
import { getObliczenia } from 'services/slices/obliczeniaSlice/selectors';
import { Select } from 'components/Select/Select';
import { setPeriodIndex, setViewMode } from 'services/slices/uiSlice';

import { SelectOption } from 'components/Select/types'; // Import SelectOption

type PeriodProps = {
  className?: string;
};

export const Period = memo(({ className }: PeriodProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { viewMode, periodIndex } = useAppSelector(state => state.ui);
  const { selectedYear } = useAppSelector(getObliczenia);

  const selectedPeriodOption = useMemo(
    () => PERIOD_OPTIONS.find(el => el.value === viewMode) || PERIOD_OPTIONS[0],
    [viewMode],
  );

  useEffect(() => {
    const targetValue = selectedPeriodOption.values[periodIndex];
    let start = 0;
    let end = 8759; // Default for YEAR
    let currentTargetValue: string | number = targetValue; // Use targetValue directly

    switch (viewMode) {
      case PeriodEnum.QUATER: {
        const monthInQuater = getMonths(selectedYear).filter(el => el.quater === targetValue);
        start = monthInQuater[0].duration.start;
        end = monthInQuater[2].duration.end;
        break;
      }
      case PeriodEnum.MONTTH: {
        const month = getMonths(selectedYear)[periodIndex];
        start = month.duration.start;
        end = month.duration.end;
        currentTargetValue = periodIndex + 1; // Month number is index + 1
        break;
      }
      case PeriodEnum.YEAR: {
        // Already defaulted
        break;
      }
    }

    // This is passed to LineEnergyProduction's filterByPeriod and useLineOptions
    // The LineEnergyProduction component will construct its own PeriodType based on Redux state
    // For now, this component primarily dispatches changes to Redux.
  }, [periodIndex, viewMode, i18n.language, selectedYear, selectedPeriodOption.values]); // Added selectedPeriodOption.values to dependencies

  const handleChangePeriod = (periodOption: SelectOption) => {
    dispatch(setViewMode(periodOption.value as PeriodEnum));
    // periodIndex is reset to 0 in setViewMode reducer
  };

  const handlePrev = () => {
    const prevIndex = periodIndex - 1;
    if (prevIndex < 0) {
      return;
    }
    dispatch(setPeriodIndex(prevIndex));
  };

  const handleNext = () => {
    const nextIndex = periodIndex + 1;
    if (nextIndex >= selectedPeriodOption.values.length) {
      return;
    }
    dispatch(setPeriodIndex(nextIndex));
  };

  const getPeriodText = () => {
    if (!selectedPeriodOption.values[periodIndex]) {
      return '---';
    }
    if (selectedPeriodOption.value === PeriodEnum.MONTTH) {
      const { name } = getMonths(selectedYear)[periodIndex]!;
      return t(name);
    }
    return selectedPeriodOption.values[periodIndex];
  };

  return (
    <SCnt className={className}>
      <SPeriod>
        <Select
          menuStickCorner="right"
          options={PERIOD_OPTIONS.map(el => ({ ...el, label: t(el.label) || el.label }))}
          value={selectedPeriodOption}
          onChange={handleChangePeriod}
          displayValue={<span>{t(selectedPeriodOption.label) || selectedPeriodOption.label}</span>}
          variant="outlined"
        />
      </SPeriod>
      {selectedPeriodOption.value !== PeriodEnum.YEAR && (
        <>
          <Separator>–</Separator>
          <SChangeWrapper>
            <SChangeButton disabled={periodIndex - 1 < 0} onClick={handlePrev}>
              <ArrowLeft />
            </SChangeButton>
            <SPeriodValue isMonth={viewMode === PeriodEnum.MONTTH}>{getPeriodText()}</SPeriodValue>
            <SChangeButton disabled={periodIndex + 1 >= selectedPeriodOption.values.length} onClick={handleNext}>
              <ArrowRight />
            </SChangeButton>
          </SChangeWrapper>
        </>
      )}
    </SCnt>
  );
});
